/**
 * Match-from-GR: find open invoices for a receipted order using the same
 * Match rules as the Invoices tab (no second engine).
 *
 * Step 11 — Actions also distinguish invoice-reconciled (lifecycle) from
 * receipted (delivery). Receipted ≠ reconciled.
 *
 * Step 5 — Chooser lists open + closed leftovers; Remove clears wrong PO.
 */

import {
  canShowMatchAction,
  getInvoiceMatchActionMeta,
  getMatchModalActionPresentation,
  MATCH_ACTION_PRIMARY,
} from './invoiceMatchActionHelpers'
import { getGoodsReceiptingStatusDisplay } from './goodsReceiptingStatusHelpers'
import {
  formatInvoiceTotalDisplay,
  formatSupplierTitleDisplay,
  getInvoiceLifecycleStatusDisplay,
  getInvoiceMatchingDisplayLabel,
} from './invoiceUploadHelpers'
import { canShowRemoveFromPoAction } from './invoiceRemoveFromPoHelpers'
import {
  getAllocationSliceDisplayForPo,
  getInvoicePageCount,
  getMultiPoAllocationSummary,
} from './invoiceMultiDocRules'
import {
  getLinkedInvoicesForReceiptingOrder,
  isOrderInvoiceReconciled,
} from './reconciliationHealth'
import { buildPoNetAmbientSummary } from './creditNoteDocumentHelpers'

const TERMINAL_INVOICE_LIFECYCLES = new Set(['reconciled', 'sentToXero'])

/** Shared GR CTA — keep distinct from Invoices “Add Invoices / Credit Notes”. */
export const GR_ADD_INVOICE_BUTTON_LABEL = 'Add invoice'

/**
 * Where Add invoice / Match should route from the Actions column.
 * - add_invoice_chooser → Link existing | Upload new
 * - match → Match modal or leftover Match chooser
 * - none → no invoice CTA (not receipted, or already invoice-reconciled)
 */
export const GR_ADD_INVOICE_ENTRY = {
  ADD_INVOICE_CHOOSER: 'add_invoice_chooser',
  MATCH: 'match',
  NONE: 'none',
}

export const GR_RECEIPTING_STATUS_COPY = {
  receiptedVsReconciled:
    'Receipted is delivery truth; Reconciled means the invoice match is finished.',
  softLinkedNotReconciled:
    'Invoice is linked (soft-link / Invoice attached) but not reconciled yet. Open Match & reconcile to finish — Link only will not close it.',
  addInvoiceTooltip:
    'No open invoice for this PO yet — link an existing one from Invoices & Credit Notes, or upload a new file. Receipted delivery does not mean the invoice is reconciled.',
}

function formatPageLabel(invoice = {}) {
  const pageCount = getInvoicePageCount(invoice)
  if (pageCount <= 1) return ''
  return `${pageCount} pages`
}

function penniesFromMatchSummary(invoice = {}) {
  const value = invoice?.matchSummary?.remainingBalancePennies
  if (value == null || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

/**
 * Ambient remaining hint (C3): Invoice(s) · Credit(s) · Remaining when both
 * document types are on the PO; CN-only → waiting for invoice; else outstanding.
 */
export function buildGoodsReceiptingRemainingHint(
  order = {},
  invoiceRows = [],
) {
  const linked = getLinkedInvoicesForReceiptingOrder(order, invoiceRows)
  const candidates = getMatchableInvoicesForReceiptingOrder(order, invoiceRows)
  const focus = candidates[0] || linked[0]
  const remaining = focus ? penniesFromMatchSummary(focus) : null

  const netAmbient = buildPoNetAmbientSummary({
    linkedDocuments: linked,
    remainingPennies: remaining,
  })
  if (netAmbient) return netAmbient

  if (remaining == null || remaining <= 0) return ''
  return `${formatInvoiceTotalDisplay(remaining)} still outstanding on this PO`
}

export function buildGoodsReceiptingRemainingHintShort(
  order = {},
  invoiceRows = [],
) {
  const full = buildGoodsReceiptingRemainingHint(order, invoiceRows)
  if (!full) return ''
  // Drop the trailing “on this PO” for the Actions column.
  return full.replace(/\s+on this PO$/, '')
}

/**
 * Chooser / Invoice-column secondary lines for one invoice row.
 */
export function getGoodsReceiptingInvoiceAmbientLines(
  invoice = {},
  purchaseOrderNumber = '',
  { linkedDocuments = [] } = {},
) {
  const pageLabel = formatPageLabel(invoice)
  const sliceLabel = getAllocationSliceDisplayForPo(
    invoice,
    purchaseOrderNumber,
  )
  const remainingPennies = penniesFromMatchSummary(invoice)
  const docsForNet =
    linkedDocuments.length > 0 ? linkedDocuments : invoice ? [invoice] : []
  const netAmbient = buildPoNetAmbientSummary({
    linkedDocuments: docsForNet,
    remainingPennies,
  })
  const remainingLabel =
    netAmbient ||
    (remainingPennies != null && remainingPennies > 0
      ? `${formatInvoiceTotalDisplay(remainingPennies)} remaining`
      : '')

  return {
    pageCount: getInvoicePageCount(invoice),
    pageLabel,
    sliceLabel,
    remainingLabel,
  }
}

function normalisePo(value) {
  return String(value || '').trim()
}

function invoiceIdKey(invoice = {}) {
  return String(invoice.id || invoice._id || '')
}

function invoiceTouchesPurchaseOrder(invoice = {}, po = '', orderIds = []) {
  if (!po && !orderIds.length) return false

  if (po && normalisePo(invoice.purchaseOrderNumber) === po) return true

  const matchedPos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers.map(normalisePo).filter(Boolean)
    : []
  if (po && matchedPos.includes(po)) return true

  const allocatedPos = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations
        .map(row => normalisePo(row?.purchaseOrderNumber))
        .filter(Boolean)
    : []
  if (po && allocatedPos.includes(po)) return true

  const summaryPo = normalisePo(invoice.matchSummary?.purchaseOrderNumber)
  if (po && summaryPo === po) return true

  const linkedId = invoice.linkedOrderId ? String(invoice.linkedOrderId) : ''
  if (linkedId && orderIds.includes(linkedId)) return true

  const matchedIds = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.map(id => String(id))
    : []
  if (matchedIds.some(id => orderIds.includes(id))) return true

  const allocatedOrderIds = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations
        .map(row => (row?.orderId ? String(row.orderId) : ''))
        .filter(Boolean)
    : []
  if (allocatedOrderIds.some(id => orderIds.includes(id))) return true

  return false
}

function collectOrderLineIds(order = {}) {
  const ids = []
  if (order?._id) ids.push(String(order._id))
  if (Array.isArray(order?.orders)) {
    order.orders.forEach(line => {
      if (line?._id) ids.push(String(line._id))
      if (line?.order) ids.push(String(line.order))
      if (line?.orderId) ids.push(String(line.orderId))
    })
  }
  return [...new Set(ids.filter(Boolean))]
}

function isTerminalInvoiceLifecycle(invoice = {}) {
  return TERMINAL_INVOICE_LIFECYCLES.has(invoice.status || '')
}

/**
 * GR order.attachedInvoiceDetails may carry authoritative lifecycle (status)
 * from the receipting API. Prefer terminal statuses over a stale Invoices
 * library row that still says attachedToPO / Linked.
 */
function getAttachedInvoiceLifecycleById(order = {}) {
  const byId = new Map()
  const details = Array.isArray(order?.attachedInvoiceDetails)
    ? order.attachedInvoiceDetails
    : []
  details.forEach(detail => {
    const id = String(detail?.id || detail?._id || '')
    if (!id) return
    const status = String(detail?.status || detail?.lifecycle || '').trim()
    if (!status) return
    byId.set(id, status)
  })
  return byId
}

function withOrderAttachedLifecycle(invoice = {}, lifecycleById) {
  if (!lifecycleById || lifecycleById.size === 0) return invoice
  const id = invoiceIdKey(invoice)
  if (!id || !lifecycleById.has(id)) return invoice
  const fromOrder = lifecycleById.get(id)
  // Only override when the order says the invoice is already closed —
  // keeps soft-link (attachedToPO) honest until Match & reconcile finishes.
  if (!TERMINAL_INVOICE_LIFECYCLES.has(fromOrder)) return invoice
  if ((invoice.status || '') === fromOrder) return invoice
  return { ...invoice, status: fromOrder }
}

function enrichInvoiceRowsWithOrderLifecycle(order = {}, invoiceRows = []) {
  const lifecycleById = getAttachedInvoiceLifecycleById(order)
  if (!lifecycleById.size) return invoiceRows || []
  return (invoiceRows || []).map(invoice =>
    withOrderAttachedLifecycle(invoice, lifecycleById),
  )
}

function isInvoiceAlreadyAttached(invoice = {}) {
  return (invoice.status || '') === 'attachedToPO'
}

function formatInvoiceShortLabel(invoice = {}) {
  const number = invoice.invoiceNumber
    ? `#${invoice.invoiceNumber}`
    : 'an invoice'
  const supplier = formatSupplierTitleDisplay(invoice.supplierTitle)
  if (supplier && supplier !== 'No supplier') {
    return `${number} (${supplier})`
  }
  return number
}

/** Whether this GR row should show the Match CTA (receipted). */
export function canShowMatchOnGoodsReceiptingOrder(order = {}) {
  return getGoodsReceiptingStatusDisplay(order?.receiptingStatus).isReceipted
}

/**
 * Open invoices that can be matched to this GR order (same canShowMatchAction
 * gate as the Invoices tab).
 *
 * @param {object} order - GR table row
 * @param {object[]} invoiceRows - mapped invoice table rows
 */
export function getMatchableInvoicesForReceiptingOrder(
  order = {},
  invoiceRows = [],
) {
  if (!canShowMatchOnGoodsReceiptingOrder(order)) return []

  const po = normalisePo(order.purchaseOrderNumber)
  const orderIds = collectOrderLineIds(order)
  if (!po && !orderIds.length) return []

  const rows = enrichInvoiceRowsWithOrderLifecycle(order, invoiceRows)

  return rows.filter(invoice => {
    if (!canShowMatchAction(invoice)) return false
    return invoiceTouchesPurchaseOrder(invoice, po, orderIds)
  })
}

/**
 * Invoices already closed against this PO (reconciled / sent to Xero).
 */
export function getClosedInvoicesForReceiptingOrder(
  order = {},
  invoiceRows = [],
) {
  const po = normalisePo(order.purchaseOrderNumber)
  const orderIds = collectOrderLineIds(order)
  if (!po && !orderIds.length) return []

  const rows = enrichInvoiceRowsWithOrderLifecycle(order, invoiceRows)

  return rows.filter(invoice => {
    if (invoice?.isArchived) return false
    if (!isTerminalInvoiceLifecycle(invoice)) return false
    return invoiceTouchesPurchaseOrder(invoice, po, orderIds)
  })
}

/**
 * Chooser list: open matchables first, then reconciled leftovers for context.
 */
export function getChooserInvoicesForReceiptingOrder(
  order = {},
  invoiceRows = [],
) {
  const open = getMatchableInvoicesForReceiptingOrder(order, invoiceRows)
  const closed = getClosedInvoicesForReceiptingOrder(order, invoiceRows)
  const seen = new Set()
  const combined = []

  ;[...open, ...closed].forEach(invoice => {
    const key = invoiceIdKey(invoice)
    if (!key || seen.has(key)) return
    seen.add(key)
    combined.push(invoice)
  })

  return combined
}

/**
 * Open chooser when multiple matchables, or one leftover while another is
 * already reconciled (Instock-class: wrong pending still keeps Match).
 */
export function shouldOpenMatchChooserForReceiptingOrder(
  order = {},
  invoiceRows = [],
) {
  const candidates = getMatchableInvoicesForReceiptingOrder(order, invoiceRows)
  if (candidates.length > 1) return true
  if (!candidates.length) return false
  return getClosedInvoicesForReceiptingOrder(order, invoiceRows).length > 0
}

export function getChooserInvoiceStatusLabel(invoice = {}) {
  if (isTerminalInvoiceLifecycle(invoice)) {
    return getInvoiceLifecycleStatusDisplay(invoice).label || 'Reconciled'
  }
  const multiPo = getMultiPoAllocationSummary(invoice)
  if (multiPo.isMultiPo && (invoice.status || '') === 'attachedToPO') {
    return multiPo.label
  }
  return getInvoiceMatchingDisplayLabel(invoice) || 'Review needed'
}

export function buildGoodsReceiptingLeftoverHint(candidates = [], closed = []) {
  if (!candidates.length || !closed.length) return ''

  const leftover = formatInvoiceShortLabel(candidates[0])
  const moreOpen =
    candidates.length > 1 ? ` (+${candidates.length - 1} more)` : ''
  return `${leftover}${moreOpen} still needs review — Remove if it's not this order.`
}

/** Short Actions-column line; full copy stays on tooltip via leftoverHint. */
export function buildGoodsReceiptingLeftoverHintShort(candidates = []) {
  if (!candidates.length) return ''
  const leftover = formatInvoiceShortLabel(candidates[0])
  const moreOpen = candidates.length > 1 ? ` (+${candidates.length - 1})` : ''
  return `${leftover}${moreOpen} still needs review`
}

/**
 * Compact Invoice-column rows: open leftovers first, reconciled muted.
 * Multi-page invoices show “N pages”; multi-invoice POs stay as separate lines.
 */
export function getGoodsReceiptingPoInvoiceListItems(
  order = {},
  invoiceRows = [],
) {
  const po = normalisePo(order.purchaseOrderNumber)
  return getChooserInvoicesForReceiptingOrder(order, invoiceRows).map(
    invoice => {
      const id = invoiceIdKey(invoice)
      const number = invoice.invoiceNumber
        ? `#${invoice.invoiceNumber}`
        : 'Invoice'
      const statusLabel = getChooserInvoiceStatusLabel(invoice)
      const ambient = getGoodsReceiptingInvoiceAmbientLines(invoice, po)
      const isClosed = isTerminalInvoiceLifecycle(invoice)

      const parts = [number]
      if (ambient.sliceLabel) parts.push(ambient.sliceLabel)
      else parts.push(statusLabel)
      if (ambient.pageLabel) parts.push(ambient.pageLabel)

      return {
        id,
        invoice,
        numberLabel: number,
        statusLabel,
        sliceLabel: ambient.sliceLabel,
        pageLabel: ambient.pageLabel,
        pageCount: ambient.pageCount,
        remainingLabel: ambient.remainingLabel,
        isClosed,
        canMatch: canShowMatchAction(invoice),
        lineLabel: parts.join(' · '),
      }
    },
  )
}

/**
 * Preferred invoice to open Match with when there is a single strong candidate,
 * else null (caller shows chooser when length > 1).
 */
export function getPrimaryMatchableInvoiceForReceiptingOrder(
  order = {},
  invoiceRows = [],
) {
  const candidates = getMatchableInvoicesForReceiptingOrder(order, invoiceRows)
  if (candidates.length === 1) return candidates[0]
  return null
}

/**
 * Strong = same green path as Invoices (Match & reconcile / one-click eligible).
 */
export function isStrongMatchInvoice(invoice = {}) {
  if (!canShowMatchAction(invoice)) return false
  return (
    getInvoiceMatchActionMeta(invoice).primaryAction ===
    MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE
  )
}

/**
 * Step 11 — GR Actions view-model from invoice lifecycle (not receiptingStatus).
 *
 * - reconciled: receipted + closed invoice(s) on PO, no open matchables
 * - match: receipted + open matchable invoice(s)
 * - upload: receipted + nothing to match and nothing closed
 * - none: not receipted
 */
export function getGoodsReceiptingMatchActionState(
  order = {},
  invoiceRows = [],
) {
  if (!canShowMatchOnGoodsReceiptingOrder(order)) {
    return {
      kind: 'none',
      candidates: [],
      closedInvoices: [],
      chooserInvoices: [],
      poInvoiceList: [],
      primaryInvoice: null,
      buttonLabel: '',
      tooltip: '',
      leftoverHint: '',
      leftoverHintShort: '',
      remainingHint: '',
      remainingHintShort: '',
      shouldOpenChooser: false,
      showLinkedNotReconciledHint: false,
      isInvoiceReconciled: false,
    }
  }

  const candidates = getMatchableInvoicesForReceiptingOrder(order, invoiceRows)
  const closed = getClosedInvoicesForReceiptingOrder(order, invoiceRows)
  const orderInvoiceClosed =
    Boolean(order?.sentToXero) || isOrderInvoiceReconciled(order, invoiceRows)

  // When the PO is already finance-closed, soft-linked library rows are often
  // stale (still attachedToPO). Drop those so we show Reconciled instead of
  // Finish reconcile — keep pending leftovers for review / Remove.
  const effectiveCandidates = orderInvoiceClosed
    ? candidates.filter(invoice => !isInvoiceAlreadyAttached(invoice))
    : candidates

  const chooserInvoices = getChooserInvoicesForReceiptingOrder(
    order,
    invoiceRows,
  )
  const poInvoiceList = getGoodsReceiptingPoInvoiceListItems(order, invoiceRows)
  const leftoverHint = buildGoodsReceiptingLeftoverHint(
    effectiveCandidates,
    closed,
  )
  const leftoverHintShort =
    buildGoodsReceiptingLeftoverHintShort(effectiveCandidates)
  const remainingHint = leftoverHint
    ? ''
    : buildGoodsReceiptingRemainingHint(order, invoiceRows)
  const remainingHintShort = leftoverHint
    ? ''
    : buildGoodsReceiptingRemainingHintShort(order, invoiceRows)
  const shouldOpenChooser =
    effectiveCandidates.length > 1 ||
    (effectiveCandidates.length === 1 && closed.length > 0)

  if (!effectiveCandidates.length) {
    // Prefer closed library rows; also honour order.sentToXero / attached
    // invoice details when the Invoices list is empty or still stale.
    if (closed.length > 0 || orderInvoiceClosed) {
      const invoiceLabel = closed[0]?.invoiceNumber
        ? `Invoice #${closed[0].invoiceNumber} is reconciled for this PO.`
        : 'Invoice is reconciled for this PO.'
      return {
        kind: 'reconciled',
        candidates: [],
        closedInvoices: closed,
        chooserInvoices,
        poInvoiceList,
        primaryInvoice: null,
        buttonLabel: '',
        tooltip: `${invoiceLabel} ${GR_RECEIPTING_STATUS_COPY.receiptedVsReconciled}`,
        leftoverHint: '',
        leftoverHintShort: '',
        remainingHint: '',
        remainingHintShort: '',
        shouldOpenChooser: false,
        showLinkedNotReconciledHint: false,
        isInvoiceReconciled: true,
      }
    }

    return {
      kind: 'upload',
      candidates: [],
      closedInvoices: [],
      chooserInvoices: [],
      poInvoiceList: [],
      primaryInvoice: null,
      buttonLabel: GR_ADD_INVOICE_BUTTON_LABEL,
      tooltip: GR_RECEIPTING_STATUS_COPY.addInvoiceTooltip,
      leftoverHint: '',
      leftoverHintShort: '',
      remainingHint: '',
      remainingHintShort: '',
      shouldOpenChooser: false,
      showLinkedNotReconciledHint: false,
      isInvoiceReconciled: false,
    }
  }

  const primaryInvoice =
    effectiveCandidates.length === 1
      ? effectiveCandidates[0]
      : getPrimaryMatchableInvoiceForReceiptingOrder(order, invoiceRows)

  const focusInvoice = primaryInvoice || effectiveCandidates[0]
  const matchAction = getInvoiceMatchActionMeta(focusInvoice)
  const canMatchAndReconcile =
    matchAction.primaryAction === MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE
  const presentation = getMatchModalActionPresentation(focusInvoice, {
    canMatchAndReconcile,
    selectedIsRecommended: true,
  })
  const anyAttached = effectiveCandidates.some(isInvoiceAlreadyAttached)
  const anyStrong = effectiveCandidates.some(isStrongMatchInvoice)

  let buttonLabel = presentation.tableButtonLabel || 'Match'
  if (effectiveCandidates.length === 1 && anyAttached && canMatchAndReconcile) {
    buttonLabel = 'Match & reconcile'
  } else if (effectiveCandidates.length === 1 && anyAttached) {
    buttonLabel = 'Finish reconcile'
  } else if (effectiveCandidates.length === 1 && anyStrong && !closed.length) {
    buttonLabel = presentation.tableButtonLabel || 'Match & reconcile'
  } else if (effectiveCandidates.length > 1 || closed.length > 0) {
    buttonLabel = 'Match'
  }

  let tooltip =
    presentation.bannerGuidance ||
    matchAction.guidance ||
    'Match an invoice to this receipted order (same as Invoices).'
  if (leftoverHint) {
    tooltip = leftoverHint
  } else if (anyAttached && !canMatchAndReconcile) {
    tooltip =
      matchAction.reason === 'multiple_matches'
        ? 'Invoice is linked but more than one order shares this PO. Open Match, confirm the correct order, then Match & reconcile — Link alone does not finish it.'
        : matchAction.reason === 'unprocessed'
        ? 'Invoice is linked but Matching has not refreshed yet. Open Finish reconcile to rematch, then Match & reconcile when totals align.'
        : GR_RECEIPTING_STATUS_COPY.softLinkedNotReconciled
  } else if (anyAttached) {
    tooltip = GR_RECEIPTING_STATUS_COPY.softLinkedNotReconciled
  }

  return {
    kind: 'match',
    candidates: effectiveCandidates,
    closedInvoices: closed,
    chooserInvoices,
    poInvoiceList,
    primaryInvoice: focusInvoice,
    buttonLabel,
    tooltip,
    leftoverHint,
    leftoverHintShort,
    remainingHint,
    remainingHintShort,
    shouldOpenChooser,
    showLinkedNotReconciledHint: anyAttached && !leftoverHint,
    isInvoiceReconciled: false,
    canRemoveCandidate: effectiveCandidates.some(canShowRemoveFromPoAction),
  }
}

/**
 * Chunk 5 — lock Add invoice vs Match routing from Actions.
 *
 * @returns {{
 *   entry: string,
 *   options: string[],
 *   state: object,
 * }}
 */
export function resolveGrAddInvoiceEntry(order = {}, invoiceRows = []) {
  const state = getGoodsReceiptingMatchActionState(order, invoiceRows)

  if (state.kind === 'upload') {
    return {
      entry: GR_ADD_INVOICE_ENTRY.ADD_INVOICE_CHOOSER,
      options: ['link_existing', 'upload_new'],
      state,
    }
  }

  if (state.kind === 'match') {
    return {
      entry: GR_ADD_INVOICE_ENTRY.MATCH,
      options: state.shouldOpenChooser ? ['match_chooser'] : ['match_modal'],
      state,
    }
  }

  return {
    entry: GR_ADD_INVOICE_ENTRY.NONE,
    options: [],
    state,
  }
}

/**
 * After Save receipt: decide toast vs open Match/picker.
 * - exactly one matchable AND strong → strong_single (toast CTA, never silent)
 * - otherwise any matchable → open_match (Match modal or chooser)
 * - none → none
 *
 * Multi-invoice POs never get a strong_single toast (no false-toast).
 * Already invoice-reconciled → none (no Match toast).
 */
export function getPostReceiptMatchOffer(order = {}, invoiceRows = []) {
  // After save the row is receipted even if the in-memory status lags.
  const receiptedOrder = {
    ...order,
    receiptingStatus:
      order?.receiptingStatus && order.receiptingStatus !== 'Not Saved'
        ? order.receiptingStatus
        : 'Saved',
  }

  const action = getGoodsReceiptingMatchActionState(receiptedOrder, invoiceRows)
  if (
    action.kind === 'reconciled' ||
    action.kind === 'none' ||
    action.kind === 'upload'
  ) {
    return { kind: 'none', candidates: [] }
  }

  const candidates = action.candidates
  if (
    candidates.length === 1 &&
    isStrongMatchInvoice(candidates[0]) &&
    !(action.closedInvoices || []).length
  ) {
    return {
      kind: 'strong_single',
      invoice: candidates[0],
      candidates,
    }
  }

  return {
    kind: 'open_match',
    candidates,
    shouldOpenChooser: action.shouldOpenChooser,
  }
}
